import { Helmet } from 'react-helmet-async'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import Header from '../components/Header'
import Footer from '../components/Footer'
import MailchimpNewsletter from '../components/MailchimpNewsletter'
import LogoLoop from '../components/LogoLoop'
import {
  PlayCircle,
  ShoppingBag,
  Download,
  CalendarHeart,
  Smartphone,
  ClipboardCheck
} from 'lucide-react'

export default function HomePage() {

  return (
    <>
      <Helmet>
        <title>Biohacking Coach | Australia &amp; Worldwide | Camilla Thompson</title>
        <meta name="description" content="Australia's leading biohacker, working with clients worldwide. Camilla Thompson, author of Biohack Me, nutritionist and longevity coach. DNA methylation testing, biological age tracking and personalised health optimisation, online from anywhere." />
        <meta name="keywords" content="biohacking coach, online biohacking coach, longevity coach, international longevity coach, biohacking author, Biohack Me, DNA methylation testing, MTHFR testing, biological age testing, epigenetics, personalised nutrition, sleep optimisation coach, metabolic health coach, healthspan coach, executive health coaching, performance optimisation coach, women's health biohacking, biohacking coach Australia, longevity coach Sydney, health optimisation Australia, nationally recognised nutritionist" />
        <meta name="author" content="Camilla Thompson" />
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
        <link rel="canonical" href="https://www.biohackme.com.au/" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.biohackme.com.au/" />
        <meta property="og:title" content="Camilla Thompson | Biohacking &amp; Longevity Coach" />
        <meta property="og:description" content="Optimise Your Life One BioHack at a Time. Biohacking and longevity coaching with Camilla Thompson, author of Biohack Me, online worldwide." />
        <meta property="og:image" content="https://www.biohackme.com.au/images/camilla-main-headshot.jpg.webp" />
        <meta property="og:site_name" content="BiohackMe" />
        <meta property="og:locale" content="en_AU" />
        
        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://www.biohackme.com.au/" />
        <meta property="twitter:title" content="Camilla Thompson | Biohacking &amp; Longevity Coach" />
        <meta property="twitter:description" content="Optimise Your Life One BioHack at a Time. Biohacking and longevity coaching with Camilla Thompson, author of Biohack Me, online worldwide." />
        <meta property="twitter:image" content="https://www.biohackme.com.au/images/camilla-main-headshot.jpg.webp" />
        <meta property="twitter:site" content="@biohackmecoach" />
        <meta property="twitter:creator" content="@biohackmecoach" />
        
        {/* Additional SEO */}
        <meta name="language" content="en" />
        <meta name="distribution" content="global" />
        <meta name="rating" content="general" />
        <meta name="revisit-after" content="7 days" />
        
        {/* Schema.org JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "BiohackMe",
            "description": "Australia's leading biohacking expert and longevity coach, working with clients worldwide",
            "url": "https://www.biohackme.com.au",
            "logo": "https://www.biohackme.com.au/logo-black.png",
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "Sydney",
              "addressRegion": "NSW",
              "addressCountry": "AU"
            },
            "contactPoint": {
              "@type": "ContactPoint",
              "email": "hello@biohackme.com.au",
              "contactType": "customer service",
              "areaServed": "AU",
              "availableLanguage": "en"
            },
            "founder": {
              "@type": "Person",
              "name": "Camilla Thompson",
              "jobTitle": "Biohacking Expert, Nutritionist & Wellbeing Coach",
              "description": "Expert biohacking coach, nutritionist, and wellbeing coach specialising in personalised health optimisation strategies",
              "subjectOf": [
                { "@type": "NewsArticle", "name": "Her blood tests were 'fine' – they were wrong", "url": "https://www.news.com.au/lifestyle/health/wellbeing/her-blood-tests-were-fine-they-were-wrong/news-story/31e585ed721aca002c171d96b2cc5247", "publisher": { "@type": "Organization", "name": "News.com.au" }, "datePublished": "2026-03-13" },
                { "@type": "NewsArticle", "name": "Why the health choices you make in your mid-30s to mid-40s are critical", "url": "https://www.bodyandsoul.com.au/houseofwellness/why-the-health-choices-you-make-in-your-mid30s-to-mid40s-are-critical/news-story/634d72445b1e69f50b6c20cf44816920", "publisher": { "@type": "Organization", "name": "Body+Soul" } },
                { "@type": "NewsArticle", "name": "This 48-year-old wellbeing coach has a bio age of 38", "url": "https://www.smh.com.au/lifestyle/health-and-wellness/this-48-year-old-wellbeing-coach-has-a-bio-age-of-38-here-s-what-she-eats-in-a-day-20250617-p5m80d.html", "publisher": { "@type": "Organization", "name": "Sydney Morning Herald" } },
                { "@type": "NewsArticle", "name": "Secrets of a biohacker", "url": "https://www.dailymail.co.uk/femail/article-14749127/Secrets-biohacker-peel-five-years-body-clock-FAST-no-millionaire-budget-required.html", "publisher": { "@type": "Organization", "name": "Daily Mail" } },
                { "@type": "NewsArticle", "name": "The Price of Everlasting Health and Vitality", "url": "https://kanebridgenews.com/the-price-of-everlasting-health-and-vitality/", "publisher": { "@type": "Organization", "name": "Kanebridge News" } },
                { "@type": "NewsArticle", "name": "Biohacking for Women", "url": "https://womenlovetech.com/biohacks-for-women-how-females-are-using-tech-to-control-their-health/", "publisher": { "@type": "Organization", "name": "Women Love Tech" } },
                { "@type": "NewsArticle", "name": "Affordable Health Tips", "url": "https://womenshealth.com.au/affordable-health-tips-biohacking/", "publisher": { "@type": "Organization", "name": "Women's Health Australia" } },
                { "@type": "NewsArticle", "name": "10 Biohacks to Transform Your Health", "url": "https://musepaper.au/wellness/10-biohacks-to-transform-your-health-routine", "publisher": { "@type": "Organization", "name": "Muse Paper" } },
                { "@type": "NewsArticle", "name": "Are You a Rushing Woman?", "url": "https://ramonamag.com/2025/06/are-you-a-rushing-woman-it-could-be-taking-years-off-your-life/", "publisher": { "@type": "Organization", "name": "Ramona Magazine" } },
                { "@type": "NewsArticle", "name": "From Mold Poisoning to Reversing Age", "url": "https://www.thelight.com.au/news/from-mold-poisoning-to-reversing-age-how-camilla-thompson-biohacked-her-way-back-to-health", "publisher": { "@type": "Organization", "name": "The Light" } },
                { "@type": "NewsArticle", "name": "How to Sleep Better and Live Healthy", "url": "https://www.thesenior.com.au/story/8955962/how-to-sleep-better-at-night-eat-better-and-live-a-healthy-life/", "publisher": { "@type": "Organization", "name": "The Senior" } },
                { "@type": "TVEpisode", "name": "Biohacking Basics", "url": "https://7news.com.au/video/the-morning-show/biohacking-basics-bc-6373764239112", "partOfSeries": { "@type": "TVSeries", "name": "The Morning Show" }, "publisher": { "@type": "Organization", "name": "7News" } },
                { "@type": "PodcastEpisode", "name": "SWIISH Wellness Unlocked", "url": "https://podcasts.apple.com/au/podcast/swiish-wellness-unlocked/id1827844661?i=1000730856816" },
                { "@type": "PodcastEpisode", "name": "Biohacker Babes", "url": "https://podcasts.apple.com/au/podcast/renee-belz-lauren-sambataro/id1470189843?i=1000750999194" },
                { "@type": "PodcastEpisode", "name": "Dr Avi Charlton", "url": "https://podcasts.apple.com/au/podcast/episode-122-biohackme-with-camilla-thompson/id1743380029?i=1000749377550" },
                { "@type": "PodcastEpisode", "name": "Biohacking with Brittany", "url": "https://podcasts.apple.com/au/podcast/biohacking-with-brittany/id1466703200?i=1000731782155" },
                { "@type": "PodcastEpisode", "name": "Unstress with Dr Ron Ehrlich", "url": "https://podcasts.apple.com/au/podcast/unstress-with-dr-ron-ehrlich/id1333795919?i=1000679644767" },
                { "@type": "PodcastEpisode", "name": "Well Hello Anxiety with Dr Jodi Richardson", "url": "https://podcasts.apple.com/au/podcast/well-hello-anxiety-with-dr-jodi-richardson/id1585382774?i=1000712290311" },
                { "@type": "PodcastEpisode", "name": "Space and Grace", "url": "https://podcasts.apple.com/au/podcast/space-and-grace-podcast-abundant-business-without-burnout/id1776927699?i=1000710666463" },
                { "@type": "PodcastEpisode", "name": "The Dynamic Leader", "url": "https://podcasts.apple.com/au/podcast/the-dynamic-leader-podcast/id1500435928?i=1000725784936" },
                { "@type": "PodcastEpisode", "name": "The Hustle Rebellion", "url": "https://podcasts.apple.com/au/podcast/the-hustle-rebellion/id1498288852?i=1000714417499" }
              ]
            },
            "sameAs": [
              "https://www.instagram.com/biohackmecoach/",
              "https://www.facebook.com/profile.php?id=61556971331791",
              "https://www.youtube.com/channel/UCMpV7HCXEyIOxuUgK0U5blw",
              "https://www.tiktok.com/@biohackmecoach"
            ],
            "areaServed": [
              { "@type": "Country", "name": "Australia" },
              { "@type": "Place", "name": "Worldwide" }
            ],
            "serviceType": [
              "Health Coaching",
              "Biohacking Consultation",
              "DNA Methylation Testing",
              "MTHFR Genetic Testing",
              "Functional Medicine",
              "Personalized Nutrition",
              "Sleep Optimization",
              "Longevity Coaching",
              "Executive Wellbeing Coaching",
              "Corporate Wellness",
              "Speaking Engagements",
              "Health Retreats",
              "Performance Optimization",
              "Metabolic Health Coaching"
            ],
            "priceRange": "$$-$$$",
            "makesOffer": [
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "DNA Methylation Testing Package",
                  "description": "Comprehensive genetic testing with personalized health plan"
                },
                "price": "699",
                "priceCurrency": "AUD"
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "3-Month Health Transformation Program",
                  "description": "Optimise Your Life - Personalized biohacking coaching"
                }
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Executive Wellbeing Coaching",
                  "description": "Leadership coaching for sustainable performance"
                },
                "price": "2500",
                "priceCurrency": "AUD"
              }
            ],
            "hasProduct": {
              "@type": "Book",
              "name": "Biohack Me",
              "author": {
                "@type": "Person",
                "name": "Camilla Thompson"
              },
              "description": "Biohacking for every age and gender - Making biohacking accessible to everyone",
              "genre": "Health & Wellness",
              "isbn": "978-1-234-56789-0",
              "publisher": {
                "@type": "Organization",
                "name": "BiohackMe Publishing"
              },
              "datePublished": "2024",
              "inLanguage": "en-AU"
            }
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [{
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://www.biohackme.com.au"
            }]
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "What is biohacking?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Biohacking is the practice of using science, data, and self-experimentation to take control of and optimise your own biology. It ranges from simple lifestyle changes like cold exposure and sleep optimisation to advanced techniques like DNA methylation testing and biological age testing. BiohackMe in Australia offers personalised biohacking coaching to help you optimise your health based on your unique genetic profile."
                }
              },
              {
                "@type": "Question",
                "name": "Who is Camilla Thompson?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Camilla Thompson is Australia's leading biohacking expert and the founder of BiohackMe. Based in Sydney, she specialises in personalised health optimisation using DNA testing, biological age testing, and evidence-based biohacking protocols. She is the author of 'Biohack Me: The Practical Guide to Everyday Biohacks' and has been featured in News.com.au, Sydney Morning Herald, Daily Mail, 7News The Morning Show, and 21+ health podcasts."
                }
              },
              {
                "@type": "Question",
                "name": "What is biohacking in Australia?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Biohacking in Australia is a growing movement focused on health optimisation through personalised science-backed strategies. BiohackMe, founded by Camilla Thompson in Sydney, is Australia's leading biohacking platform offering one-on-one coaching, DNA health packages, masterclasses, corporate wellness talks, and retreats. Coaching is delivered online worldwide, with keynotes and retreats internationally."
                }
              },
              {
                "@type": "Question",
                "name": "How can I reverse my biological age?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Biological age can be improved through targeted lifestyle interventions including optimised sleep, personalised nutrition based on your DNA, stress management, cold and heat exposure therapy, exercise protocols, and gut health optimisation. BiohackMe's 'The Upgrade' program uses GlycanAge biological age testing and MyDNA genetic testing to create personalised protocols, with clients typically seeing 5-7 years improvement in biological age within 12 weeks."
                }
              },
              {
                "@type": "Question",
                "name": "What is the best biohacking book in Australia?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "'Biohack Me: The Practical Guide to Everyday Biohacks' by Camilla Thompson covers the top 10 biohacks, biohacking on a budget, biohacking for women, biohacking for men, and biohacking technologies. It is available at biohackme.com.au and is designed to make biohacking accessible to everyone regardless of budget or experience level."
                }
              }
            ]
          })}
        </script>
      </Helmet>

      <Header />

      {/* Hero Section - Side by Side Layout */}
      <section className="bg-white pt-20 sm:pt-24 md:pt-28 lg:pt-32 pb-12 sm:pb-16 lg:pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center">

            {/* Content Side - Left */}
            <div className="space-y-4 sm:space-y-6 lg:space-y-8 order-2 lg:order-1">
              {/* Credential line above the headline, matching the /talks pattern:
                  gold uppercase kicker, then the headline, then the tagline. */}
              <p className="hidden lg:block text-sm xl:text-base font-bold uppercase tracking-[0.18em] text-gold-dark text-center lg:text-left">
                International Keynote Speaker <span className="text-charcoal/30">|</span> Author <span className="text-charcoal/30">|</span> Coach
              </p>

              {/* Main Headline - Hidden on mobile, shown on desktop */}
              <h1 className="sr-only lg:not-sr-only lg:block text-3xl xs:text-4xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold leading-[1.05] tracking-tight text-ocean text-center lg:text-left">
                Future Proof<br />Your <span className="text-gold">Health.</span>
              </h1>

              {/* Brand tagline */}
              <p className="hidden lg:block text-xl lg:text-2xl font-semibold tracking-tight text-ocean text-center lg:text-left">
                Age Well. Feel Better. <span className="text-gold-dark">Live Longer.</span>
              </p>

              {/* Supporting line - Hidden on mobile, shown on desktop */}
              <p className="hidden lg:block text-lg sm:text-xl lg:text-2xl text-charcoal/75 leading-relaxed text-center lg:text-left font-light">
                Australia's leading biohacker. Making biohacking <span className="text-ocean font-semibold">accessible</span>, <span className="text-ocean font-semibold">affordable</span> &amp; <span className="text-ocean font-semibold">achievable</span> for all.
              </p>

              {/* CTA Buttons — primary + secondary, with text-link row below */}
              <div className="flex flex-wrap gap-3 pt-2 justify-center lg:justify-start">
                <a
                  href="https://www.amazon.com.au/Biohack-Me-Practical-Everyday-Longevity/dp/1394334036?linkCode=sl1&tag=camillathomps-22&language=en_AU&ref_=as_li_ss_tl"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn--gold btn--sm"
                >
                  <ShoppingBag className="w-5 h-5" />
                  Buy My Book
                </a>

                <a
                  href="https://calendly.com/thewellnesscoachsession/biohackme"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn--primary btn--sm"
                >
                  <CalendarHeart className="w-4 h-4" />
                  Book a Free Health Audit
                </a>
              </div>

              {/* Secondary actions as quiet text links. Boxing them made them
                  compete with the two real CTAs and look cheap. */}
              <div className="flex flex-wrap items-center gap-x-6 gap-y-3 pt-2 justify-center lg:justify-start text-sm">
                <a
                  href="/freebie"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-ocean/80 hover:text-ocean border-b-2 border-gold/60 hover:border-gold pb-1 transition-colors font-medium"
                >
                  <Download className="w-4 h-4 flex-shrink-0" strokeWidth={1.75} />
                  Free Guide
                </a>
                <a
                  href="/biohack-assessment"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-ocean/80 hover:text-ocean border-b-2 border-gold/60 hover:border-gold pb-1 transition-colors font-medium"
                >
                  <ClipboardCheck className="w-4 h-4 flex-shrink-0" strokeWidth={1.75} />
                  Health Assessment
                </a>
                <a
                  href="https://biohackme-method-app.web.app"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-ocean/80 hover:text-ocean border-b-2 border-gold/60 hover:border-gold pb-1 transition-colors font-medium"
                >
                  <Smartphone className="w-4 h-4 flex-shrink-0" strokeWidth={1.75} />
                  Open the App
                </a>
              </div>

            </div>

            {/* Hero Image Side - Right */}
            <div className="relative order-1 lg:order-2 mb-6 lg:mb-0">
              {/* Mobile Hero Text - Only visible on mobile */}
              <div className="lg:hidden text-center mb-6">
                <p className="text-xs sm:text-sm font-bold uppercase tracking-[0.14em] text-gold-dark mb-3 leading-relaxed">
                  International Keynote Speaker <span className="text-charcoal/30">|</span> Author <span className="text-charcoal/30">|</span> Coach
                </p>
                <p aria-hidden="true" className="text-3xl sm:text-4xl md:text-5xl font-bold leading-[1.05] tracking-tight text-ocean">
                  Future Proof<br />Your <span className="text-gold">Health.</span>
                </p>
                <p className="text-lg sm:text-xl font-semibold tracking-tight text-ocean mt-3">
                  Age Well. Feel Better. <span className="text-gold-dark">Live Longer.</span>
                </p>
                <p className="text-base sm:text-lg leading-relaxed mt-4 text-charcoal/75 font-light">
                  Australia's leading biohacker. Making biohacking <span className="text-ocean font-semibold">accessible</span>, <span className="text-ocean font-semibold">affordable</span> &amp; <span className="text-ocean font-semibold">achievable</span> for all.
                </p>
              </div>

              <div className="relative">
                <img
                  src="/images/hero%20homepage.webp"
                  alt="Camilla Thompson - BiohackMe Coach"
                  width="800"
                  height="1200"
                  className="w-full h-auto max-w-[240px] sm:max-w-xs lg:max-w-sm xl:max-w-sm mx-auto object-cover rounded-3xl shadow-sm"
                  loading="eager"
                  fetchPriority="high"
                />
              </div>
            </div>

          </div>

                {/* Media Logos Row. Sits below the whole hero grid so it spans the
                    full width, under the photo as well as the copy. */}
                <div className="pt-8 sm:pt-10 mt-2 border-t border-ocean/10">
                  <p className="text-xs sm:text-sm text-gold-dark mb-5 sm:mb-6 font-bold tracking-[0.2em] text-center uppercase">As Featured In</p>
                  <div className="block sm:hidden">
                    {/* Mobile: Static logo grid */}
                    <div className="grid grid-cols-3 gap-4 max-w-xs mx-auto items-center">
                      <img src="/images/media-logos/forbes.png" alt="Forbes Australia" width="100" height="24" className="h-5 w-auto object-contain opacity-60 mx-auto" />
                      <img src="/images/media-logos/Sunrise.png" alt="Sunrise" width="100" height="24" className="h-6 w-auto object-contain opacity-60 mx-auto" />
                      <img src="/images/media-logos/the morning show.png" alt="The Morning Show" width="100" height="24" className="h-6 w-auto object-contain opacity-60 mx-auto" />
                      <img src="/images/media-logos/daily mail.png" alt="Daily Mail" width="100" height="24" className="h-6 w-auto object-contain opacity-60 mx-auto" />
                      <img src="/images/media-logos/SMH.png" alt="SMH" width="100" height="24" className="h-6 w-auto object-contain opacity-60 mx-auto" />
                      <img src="/images/media-logos/womens fitness.png" alt="Women's Fitness" width="100" height="24" className="h-6 w-auto object-contain opacity-60 mx-auto" />
                      <img src="/images/media-logos/womens health.png" alt="Women's Health" width="100" height="24" className="h-6 w-auto object-contain opacity-60 mx-auto" />
                      <img src="/images/media-logos/images.png" alt="The CEO Magazine" width="100" height="24" style={{ width: '66%' }} className="h-6 object-contain opacity-60 mx-auto" />
                      <img src="/images/media-logos/newscorp.png" alt="News Corp" width="100" height="24" className="h-6 w-auto object-contain opacity-60 mx-auto" />
                      <img src="/images/media-logos/daily-telegraph.png" alt="The Daily Telegraph" width="100" height="24" className="h-4 w-auto object-contain opacity-60 mx-auto" />
                      <img src="/images/media-logos/7news.png" alt="7 News" width="100" height="24" className="h-6 w-auto object-contain opacity-60 mx-auto" />
                      <img src="/images/media-logos/prevention.png" alt="Prevention" width="100" height="24" className="h-5 w-auto object-contain opacity-60 mx-auto" />
                    </div>
                  </div>
                  <div className="hidden sm:block">
                    {/* Desktop: Animated logo loop */}
                    <LogoLoop
                      logos={[
                        { src: "/images/media-logos/forbes.png", alt: "Forbes Australia" },
                        { src: "/images/media-logos/Sunrise.png", alt: "Sunrise", scale: 1.4 },
                        { src: "/images/media-logos/the morning show.png", alt: "The Morning Show" },
                        { src: "/images/media-logos/daily mail.png", alt: "Daily Mail" },
                        { src: "/images/media-logos/womens fitness.png", alt: "Women's Fitness" },
                        { src: "/images/media-logos/SMH.png", alt: "Sydney Morning Herald" },
                        { src: "/images/media-logos/womens health.png", alt: "Women's Health" },
                        { src: "/images/media-logos/images.png", alt: "CEO Magazine" },
                        { src: "/images/media-logos/daily-telegraph.png", alt: "The Daily Telegraph" },
                        { src: "/images/media-logos/bodyandsoul.svg", alt: "Body and Soul" },
                        { src: "/images/media-logos/Sunrise.png", alt: "Sunrise", scale: 1.4 },
                        { src: "/images/media-logos/womens fitness.png", alt: "Women's Fitness" },
                        { src: "/images/media-logos/the morning show.png", alt: "The Morning Show" },
                        { src: "/images/media-logos/womens health.png", alt: "Women's Health" },
                        { src: "/images/media-logos/daily mail.png", alt: "Daily Mail" }
                      ]}
                      speed={30}
                      logoHeight={36}
                      gap={50}
                      pauseOnHover={true}
                      fadeOut={true}
                      fadeOutColor="white"
                      scaleOnHover={false}
                      ariaLabel="Media outlets featuring BiohackMe"
                      className="max-w-5xl mx-auto"
                    />
                  </div>

                {/* Credibility strip. The same four claims as the media page, so
                    the numbers behind "As Featured In" are visible here too. */}
                <div className="mt-8 sm:mt-10 bg-ocean rounded-2xl px-6 py-8 sm:py-9">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-6 max-w-2xl mx-auto text-center">
                    <div>
                      <p className="text-2xl md:text-3xl font-bold text-gold">40+</p>
                      <p className="text-xs md:text-sm text-white/70 mt-1">Media features</p>
                    </div>
                    <div>
                      <p className="text-2xl md:text-3xl font-bold text-gold">30+</p>
                      <p className="text-xs md:text-sm text-white/70 mt-1">Podcasts</p>
                    </div>
                    <div>
                      <p className="text-2xl md:text-3xl font-bold text-gold">25K+</p>
                      <p className="text-xs md:text-sm text-white/70 mt-1">Social media reach</p>
                    </div>
                    <div>
                      <p className="text-2xl md:text-3xl font-bold text-gold">Top 50</p>
                      <p className="text-xs md:text-sm text-white/70 mt-1">Lifespanning Leaders</p>
                    </div>
                  </div>
                </div>
                </div>
        </div>
      </section>

      {/* Hi I'm Camilla Section - Moved up */}
      <section className="py-8 sm:py-12 md:py-20 bg-cloud">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-16 items-center">
              {/* Camilla Image */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className="relative"
              >
                <div className="relative">
                  <img
                    src="/images/camilla-about-cream.webp"
                    alt="Camilla Thompson - longevity and performance coach"
                    width="1600"
                    height="1200"
                    className="w-full max-w-xl lg:max-w-2xl mx-auto object-cover rounded-lg shadow-lg"
                    style={{objectPosition: '50% 18%', maxHeight: '600px'}}
                    loading="lazy"
                  />
                </div>

                {/* Buttons sit under the photo so the text column stays a
                    single clean block of copy. */}
                <div className="flex flex-col sm:flex-row gap-3 justify-center mt-5 sm:mt-6 max-w-xl lg:max-w-2xl mx-auto">
                  <a href="/about" className="btn btn--primary flex-1 text-center">
                    Learn More About Me
                  </a>
                  <a href="/method" className="btn btn--secondary flex-1 text-center">
                    Discover My Method
                  </a>
                </div>
              </motion.div>
              
              {/* Content */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                viewport={{ once: true }}
              >
                <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-3 sm:mb-4 text-ocean text-center lg:text-left">
                  Hi, I'm Camilla
                </h2>
                <div className="w-12 h-0.5 bg-gold mb-5 sm:mb-6 mx-auto lg:mx-0" />

                <div className="max-w-none leading-relaxed space-y-3.5 sm:space-y-4">
                  <p className="text-[15px] sm:text-base text-charcoal/75 text-center lg:text-left">
                    I'm a longevity and performance coach, nutritionist and executive coach, and the author of <em>Biohack Me</em>, Australia's first book on biohacking.
                  </p>
                  <p className="text-[15px] sm:text-base text-charcoal/75 text-center lg:text-left">
                    For the past decade I've coached high performers and tested what actually works, not what happens to be trending. What I share is what has genuinely supported them, and me, over those years. I teach it from stages around the world, on retreats and in the BiohackMe app.
                  </p>
                  <p className="text-[15px] sm:text-base text-charcoal/75 text-center lg:text-left">
                    My mission is simple. Make biohacking <span className="text-ocean font-semibold">accessible</span>, <span className="text-ocean font-semibold">affordable</span> and <span className="text-ocean font-semibold">achievable</span>. No extremes, no punishing protocols, no overwhelm. Just the handful of changes that genuinely move the needle.
                  </p>
                  <p className="text-[15px] sm:text-base text-ocean font-semibold text-center lg:text-left border-l-2 border-gold pl-4">
                    I reversed my biological age by 16 years using the BiohackMe Method.{' '}
                    <a
                      href="https://biohackme.substack.com/p/how-i-reversed-my-biological-age"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="whitespace-nowrap text-gold-dark underline underline-offset-4 hover:text-ocean transition-colors"
                    >
                      Read more
                    </a>
                  </p>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Dave Asprey Featured Quote */}
      <section className="py-12 md:py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center"
            >
              {/* Flat ice panel, no gradient. Book cover sits beside the
                  endorsement so the quote has the thing it is endorsing. */}
              <div className="bg-ice p-7 sm:p-9 md:p-12 rounded-xl border border-ocean/15 max-w-5xl mx-auto">
                <div className="grid md:grid-cols-[auto,1fr] gap-7 sm:gap-9 md:gap-12 items-center">
                  <img
                    src="https://static.wixstatic.com/media/f5168e_07fd3756f95c42368f72e5a6556609a0~mv2.png/v1/fill/w_410,h_607,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/Render%201%20(FRONT)%20-%20No%20Background_PNG.png"
                    alt="Biohack Me by Camilla Thompson"
                    width="410"
                    height="607"
                    className="w-36 sm:w-44 md:w-52 h-auto mx-auto md:mx-0 drop-shadow-xl"
                    loading="lazy"
                  />

                  <div className="text-center md:text-left">
                    <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-gold-dark mb-3">The Book</p>
                    <h2 className="text-2xl sm:text-3xl font-bold text-ocean leading-tight">
                      Biohack Me
                    </h2>
                    <p className="text-[15px] sm:text-base text-charcoal/75 leading-relaxed mt-4">
                      Australia&rsquo;s first book on biohacking. A practical guide to everyday
                      longevity, built on nearly a decade of testing what actually works rather
                      than what happens to be trending.
                    </p>

                    <p className="inline-block bg-gold text-ocean text-[10px] sm:text-xs font-bold uppercase tracking-[0.12em] px-3.5 py-2 mt-5 leading-snug">
                      Finalist <span className="text-ocean/45">&middot;</span> Australian Business Book Awards <span className="text-ocean/45">&middot;</span> Health &amp; Wellbeing
                    </p>

                    <div className="mt-6">
                      {/* whitespace-normal so the long label wraps on mobile
                          instead of overflowing past the card edge */}
                      <a
                        href="/my-book"
                        className="btn btn--primary !whitespace-normal max-w-full text-center leading-snug"
                      >
                        Read the Book Dave Recommends
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* How can we work together Section */}
      <section className="py-12 md:py-20 bg-ocean">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-5 md:mb-6">
              <span className="text-white">
                How can we work <span className="text-gold">together?</span>
              </span>
            </h2>
            <div className="w-14 h-0.5 bg-gold mx-auto" />
          </motion.div>

          <div className="grid md:grid-cols-3 gap-12 max-w-6xl mx-auto">
            {[
              {
                title: "Keynote Speaker",
                description: "The Future Proof talk series. Keynotes and workshops on longevity, energy and human performance",
                image: "/images/How we work together/Biohackme website images18.webp",
                link: "/talks",
                cta: "LEARN MORE"
              },
              {
                title: "Longevity Coaching",
                description: "Personalised longevity coaching program with custom protocols and ongoing support",
                image: "/images/How we work together/Biohackme website images13.webp",
                link: "/superchargeyourlife",
                cta: "LEARN MORE"
              },
              {
                title: "Retreats",
                description: "Destination Deluxe Award Winning Global Live Well Longer Retreats",
                image: "/images/How we work together/Biohackme website images3.webp",
                link: "/retreats",
                cta: "LEARN MORE"
              }
            ].map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                viewport={{ once: true }}
                className="text-center flex flex-col h-full"
              >
                <div className="relative mb-8">
                  <div className="w-48 h-48 sm:w-56 sm:h-56 mx-auto rounded-full overflow-hidden bg-white/5 ring-1 ring-white/15">
                    <img
                      src={service.image}
                      alt={service.title}
                      width="224"
                      height="224"
                      className="w-full h-full object-cover object-center"
                      loading="lazy"
                    />
                  </div>
                </div>
                
                <h3 className="text-3xl font-bold mb-4">
                  <span className="text-white">{service.title}</span>
                </h3>
                <p className="text-white/70 leading-relaxed mb-8 text-lg max-w-md mx-auto flex-grow">
                  {service.description}
                </p>

                <a
                  href={service.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn--secondary-on-dark mt-auto self-center"
                >
                  {service.cta}
                </a>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Corporate Testimonials Section */}
      <section className="py-12 md:py-20 bg-cloud">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            
            {/* Corporate Testimonials */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="grid md:grid-cols-3 gap-5 lg:gap-6"
            >
              {[
                {
                  tag: 'Coaching',
                  quote: 'Camilla is an absolutely fantastic wellness coach, highly recommended to anyone wanting to supercharge personal performance. She brings deep biohacking expertise into every coaching session.',
                  name: 'Carmen Bekker',
                  detail: 'Lead Partner, KPMG Consulting',
                },
                {
                  tag: 'Speaking',
                  quote: "Camilla's expertise in biohacking was the highlight of our Wellbeing Day. She delivered a captivating session blending the latest research with practical strategies.",
                  name: 'Kevin Figueiredo',
                  detail: 'Chief People & Safety Officer, Super Retail Group',
                },
                {
                  tag: 'The Book',
                  quote: 'Camilla provides a masterclass in turning back the clock. This book is a must-read if you are looking to supercharge your life through biohacking.',
                  name: 'Dave Asprey',
                  detail: 'Father of Biohacking, Founder of Bulletproof',
                },
              ].map((t) => (
                <div key={t.name} className="bg-white rounded-lg p-6 md:p-7 shadow-lg border border-ocean/10 flex flex-col">
                  <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-gold-dark mb-4">{t.tag}</p>
                  <p className="text-[15px] text-charcoal/80 italic leading-relaxed mb-6 flex-1">
                    &ldquo;{t.quote}&rdquo;
                  </p>
                  <div className="border-t border-ocean/10 pt-4">
                    <h4 className="font-semibold text-ocean">{t.name}</h4>
                    <p className="text-charcoal/60 text-[13px] leading-snug mt-0.5">{t.detail}</p>
                  </div>
                </div>
              ))}
            </motion.div>

          </div>
        </div>
      </section>

      {/* BIOHACKING WITH CAMILLA Section - With Video */}
      <section className="py-12 md:py-20 bg-ice">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-6 md:mb-8">
                <span className="text-ocean">BIOHACKING WITH</span> <span className="text-gold-dark">CAMILLA</span>
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                The Biohack Me Method addresses 8 foundational pillars — nutrition, sleep, gut health, mindset, movement, recovery, hydration and connection — to improve energy, performance and longevity.
              </p>
            </motion.div>
            
            {/* Video Section */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
              className="relative max-w-4xl mx-auto"
            >
              <div className="relative rounded-lg overflow-hidden shadow-2xl border-4 border-sky/20 bg-ice">
                <iframe
                  className="w-full aspect-video"
                  src="https://www.youtube.com/embed/b9TMLyX-dpk?start=1"
                  title="Biohacking with Camilla - Introduction Video"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                ></iframe>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Featured Essay — Latest Substack post + Method CTA */}
      <section className="bg-ocean py-16 sm:py-20 md:py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-6">
          <a
            href="https://biohackme.substack.com/p/how-i-reversed-my-biological-age"
            target="_blank"
            rel="noopener"
            className="block group bg-white rounded-2xl p-6 sm:p-8 md:p-10 border border-white/10 hover:border-sky transition-all duration-300"
          >
            <p className="text-[10px] sm:text-xs uppercase tracking-[0.2em] text-gold-dark font-bold mb-3">Featured Substack</p>
            <h2 className="text-lg sm:text-xl font-bold text-ocean leading-snug mb-3">
              How I reversed my biological age by 16 years — my GlycanAge results at 49
            </h2>
            <p className="text-[14px] text-charcoal/70 leading-relaxed">
              What the data revealed about the Biohack Me Method — and why decline after 40 is not inevitable.
            </p>
            <div className="mt-4 sm:mt-5 inline-flex items-center gap-2 text-sm font-semibold text-ocean group-hover:text-gold-dark transition-colors">
              <span>Read on Substack</span>
              <svg className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7-7 7" />
              </svg>
            </div>
          </a>

          <Link
            to="/method"
            className="block group bg-cloud rounded-2xl p-6 sm:p-8 md:p-10 border border-white/10 hover:border-sky transition-all duration-300"
          >
            <p className="text-[10px] sm:text-xs uppercase tracking-[0.2em] text-gold-dark font-bold mb-3">The Framework</p>
            <h2 className="text-lg sm:text-xl font-bold text-ocean leading-snug mb-3">
              The Biohack Me Method — 8 Pillars + 8 Life Areas
            </h2>
            <p className="text-[14px] text-charcoal/70 leading-relaxed">
              Camilla's two-tier framework that makes biohacking accessible for everyone — start with the basics, apply across your life.
            </p>
            <div className="mt-4 sm:mt-5 inline-flex items-center gap-2 text-sm font-semibold text-ocean group-hover:text-gold-dark transition-colors">
              <span>Explore the Method</span>
              <svg className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7-7 7" />
              </svg>
            </div>
          </Link>
        </div>
      </section>

      {/* Shop & Masterclass Section */}
      <section className="py-12 md:py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center mb-10"
            >
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4">
                <span className="text-ocean">Ready to</span> <span className="text-gold-dark">Take Action?</span>
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Discover the tools and knowledge you need to start your biohacking journey today.
              </p>
            </motion.div>

            <div className="grid sm:grid-cols-2 gap-5">
              {[
                {
                  href: '/shop',
                  icon: 'shop',
                  eyebrow: 'Biohacking Shop',
                  title: 'Tools & Products',
                  copy: "Camilla's curated biohacking devices, supplements and wellness products, all personally tested.",
                  cta: 'Explore the shop',
                },
                {
                  href: '/masterclass',
                  icon: 'play',
                  eyebrow: 'Masterclasses',
                  title: 'Learn the Science',
                  copy: 'Structured courses covering DNA testing, sleep optimisation, nutrition and recovery protocols.',
                  cta: 'Start learning',
                },
              ].map((card, i) => (
                <motion.a
                  key={card.href}
                  href={card.href}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: i * 0.1 }}
                  viewport={{ once: true }}
                  className="group block bg-cloud hover:bg-ice/60 rounded-2xl border border-ocean/10 hover:border-ocean/25 p-6 sm:p-7 transition-colors"
                >
                  <div className="w-11 h-11 rounded-full bg-ocean/10 flex items-center justify-center mb-4">
                    {card.icon === 'shop' ? (
                      <ShoppingBag className="w-5 h-5 text-gold-dark" strokeWidth={1.75} />
                    ) : (
                      <PlayCircle className="w-5 h-5 text-gold-dark" strokeWidth={1.75} />
                    )}
                  </div>
                  <p className="text-[0.62rem] font-semibold tracking-[0.2em] uppercase text-gold-dark mb-2">
                    {card.eyebrow}
                  </p>
                  <h3 className="text-xl font-bold text-ocean mb-2">{card.title}</h3>
                  <p className="text-charcoal/70 text-sm leading-relaxed mb-4">{card.copy}</p>
                  <span className="text-sm font-semibold text-ocean group-hover:text-gold-dark transition-colors">
                    {card.cta} &rarr;
                  </span>
                </motion.a>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* What is Biohacking - AI Definition Block */}
      <section className="py-8 md:py-10 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="bg-cloud rounded-2xl p-5 sm:p-6 border border-ocean/10"
          >
            <p className="text-[10px] uppercase tracking-[0.2em] text-gold-dark font-bold mb-1.5">In plain English</p>
            <h2 className="text-base sm:text-lg font-bold text-ocean mb-2.5">What is Biohacking?</h2>
            <p className="text-charcoal/65 text-[13px] leading-relaxed">
              Biohacking is the practice of using data, science and targeted lifestyle interventions to optimise how your body and mind perform. Unlike generic wellness advice, biohacking uses tools like DNA methylation testing, biological age tracking and personalised protocols to make evidence-based health decisions. The <a href="/method" className="text-ocean font-semibold underline underline-offset-4 hover:text-gold-dark transition-colors">Biohack Me Method</a>, created by Camilla Thompson, makes biohacking accessible for everyone through 8 foundational pillars: nutrition, sleep, gut health, mindset, movement, recovery, hydration and connection.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Get in touch - Free Guide Section */}
      <section className="py-12 md:py-20 bg-ocean">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="max-w-5xl mx-auto grid md:grid-cols-[1.25fr_0.75fr] gap-8 md:gap-12 items-center text-left"
          >
            <div>
              <h2 className="text-lg md:text-xl font-bold text-white mb-3">
                Download my FREE BIOHACKME GUIDE!{' '}
                <a
                  href="/freebie"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="whitespace-nowrap text-gold underline underline-offset-4 hover:text-white transition-colors"
                >
                  Get it here
                </a>
              </h2>

              <p className="text-[15px] text-white/70 leading-relaxed mb-6">
                I've spent a long time perfecting this guide to offer you the ideal starting point for your biohacking journey.
              </p>

              <p className="text-sm font-bold uppercase tracking-[0.12em] text-gold mb-3">What's in this guide?</p>
              <ul className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-[14px] text-white/70 mb-5">
                <li>✓ WTF is Biohacking?</li>
                <li>✓ What is a Biohacker Mindset?</li>
                <li>✓ Biohacking Framework</li>
                <li>✓ Biohacking on a Budget</li>
                <li>✓ Biohacking Technologies</li>
                <li>✓ Top 10 Biohacks</li>
              </ul>

              <p className="text-[15px] text-white/70 mb-7">
                This guide provides all the information, tips, and tricks you need to get started.
              </p>

              <a href="/freebie" target="_blank" rel="noopener noreferrer" className="btn btn--primary-on-dark">
                <Download className="w-4 h-4" />
                Free Biohacking Guide
              </a>
            </div>

            <div className="flex justify-center md:justify-end">
              <img
                src="/images/Freebie.webp"
                alt="BiohackMe Free Biohacking Starter Guide"
                width="400"
                height="350"
                className="w-full max-w-[260px] sm:max-w-xs rounded-lg shadow-lg object-contain"
                loading="lazy"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-16 bg-ice">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <MailchimpNewsletter
            title="Stay Updated with Latest Biohacking Tips"
            subtitle="Join thousands of high performers getting exclusive health optimization strategies delivered to their inbox"
            buttonText="Get Free Biohacking Tips"
            className="max-w-2xl mx-auto"
          />
        </div>
      </section>

      <Footer />
    </>
  )
}